// src/services/bulletinService.ts

import apiClient from '../utils/apiClient';

// =============================================================================
// TYPES
// =============================================================================

export interface FieldDefinition {
  name: string;
  type: 'text' | 'textarea' | 'reference' | 'boolean' | 'select' | 'number' | 'lines' | 'file';
  label: string;
  required?: boolean;
  options?: string[];
  default?: any;
}

export interface StyleDefinition {
  fontWeight?: string;
  fontStyle?: string;
  fontSize?: string;
  textAlign?: string;
  color?: string;
  marginTop?: string;
  marginBottom?: string;
  textTransform?: string;
  lineHeight?: string;
}

export interface FormattingType {
  id: number;
  slug: string;
  name: string;
  fields: FieldDefinition[];
  styles: StyleDefinition;
  latex_template: string | null;
  created_at?: string;
}

export interface BulletinElement {
  id: number;
  name: string;
  formatting_type_slug: string;
  field_values: Record<string, any>;
  tags: string[];
  is_template: boolean;
  created_at: string;
  updated_at: string;
}

export interface BulletinElementInput {
  name: string;
  formatting_type_slug: string;
  field_values: Record<string, any>;
  tags?: string[];
  is_template?: boolean;
}

export interface TemplateSlot {
  id: string;
  label: string;
  formatting_type_slug: string;
  content_mode: 'static' | 'dynamic';
  static_content?: { field_values: Record<string, any> };
  dynamic_hints?: {
    placeholder?: string;
    default_values?: Record<string, any>;
    auto_populate_from?: string;
  };
}

export interface BulletinTemplate {
  id: number;
  name: string;
  description?: string;
  page_settings: {
    paperSize?: string;
    margins?: { top: number; bottom: number; left: number; right: number };
    columns?: number;
  };
  header: {
    churchName?: string;
    serviceName?: string;
    showDate?: boolean;
    showLiturgicalInfo?: boolean;
  };
  footer: {
    showPageNumbers?: boolean;
    customText?: string;
  };
  element_slots: TemplateSlot[];
  created_at: string;
  updated_at: string;
}

export interface BulletinTemplateInput {
  name: string;
  description?: string;
  page_settings?: BulletinTemplate['page_settings'];
  header?: BulletinTemplate['header'];
  footer?: BulletinTemplate['footer'];
  element_slots: TemplateSlot[];
}

export interface BulletinElementInstance {
  id: string;
  label: string;
  formatting_type_slug: string;
  field_values: Record<string, any>;
  source_element_id?: number;
}

export interface Bulletin {
  id: number;
  name: string;
  service_id?: number;
  template_id?: number;
  date: string;
  liturgical_info?: {
    season?: string;
    day?: string;
    color?: string;
  };
  elements: BulletinElementInstance[];
  status: 'draft' | 'finalized';
  created_at: string;
  updated_at: string;
}

export interface BulletinInput {
  name?: string;
  service_id?: number;
  template_id?: number;
  date: string;
  liturgical_info?: Bulletin['liturgical_info'];
  elements: BulletinElementInstance[];
  status?: 'draft' | 'finalized';
}

// =============================================================================
// FORMATTING TYPES
// =============================================================================

export const getFormattingTypes = async (): Promise<FormattingType[]> => {
  const response = await apiClient.get<FormattingType[]>('/bulletins/formatting-types');
  return response.data;
};

export const getFormattingTypeBySlug = async (slug: string): Promise<FormattingType> => {
  const response = await apiClient.get<FormattingType>(`/bulletins/formatting-types/${slug}`);
  return response.data;
};

// =============================================================================
// BULLETIN ELEMENTS (saved content/presets)
// =============================================================================

export const getElements = async (options?: {
  includeTemplates?: boolean;
  formattingType?: string;
}): Promise<BulletinElement[]> => {
  const params: any = {};
  if (options?.includeTemplates !== undefined) {
    params.includeTemplates = options.includeTemplates;
  }
  if (options?.formattingType) {
    params.formattingType = options.formattingType;
  }
  const response = await apiClient.get<BulletinElement[]>('/bulletins/elements', { params });
  return response.data;
};

export const getElementById = async (id: number): Promise<BulletinElement> => {
  const response = await apiClient.get<BulletinElement>(`/bulletins/elements/${id}`);
  return response.data;
};

export const createElement = async (data: BulletinElementInput): Promise<BulletinElement> => {
  const response = await apiClient.post<BulletinElement>('/bulletins/elements', data);
  return response.data;
};

export const updateElement = async (id: number, data: Partial<BulletinElementInput>): Promise<BulletinElement> => {
  const response = await apiClient.put<BulletinElement>(`/bulletins/elements/${id}`, data);
  return response.data;
};

export const deleteElement = async (id: number): Promise<{ success: boolean }> => {
  const response = await apiClient.delete<{ success: boolean }>(`/bulletins/elements/${id}`);
  return response.data;
};

// =============================================================================
// BULLETIN TEMPLATES
// =============================================================================

export const getTemplates = async (): Promise<BulletinTemplate[]> => {
  const response = await apiClient.get<BulletinTemplate[]>('/bulletins/templates');
  return response.data;
};

export const getTemplateById = async (id: number): Promise<BulletinTemplate> => {
  const response = await apiClient.get<BulletinTemplate>(`/bulletins/templates/${id}`);
  return response.data;
};

export const createTemplate = async (data: BulletinTemplateInput): Promise<BulletinTemplate> => {
  const response = await apiClient.post<BulletinTemplate>('/bulletins/templates', data);
  return response.data;
};

export const updateTemplate = async (id: number, data: Partial<BulletinTemplateInput>): Promise<BulletinTemplate> => {
  const response = await apiClient.put<BulletinTemplate>(`/bulletins/templates/${id}`, data);
  return response.data;
};

export const deleteTemplate = async (id: number): Promise<{ success: boolean }> => {
  const response = await apiClient.delete<{ success: boolean }>(`/bulletins/templates/${id}`);
  return response.data;
};

// =============================================================================
// BULLETINS
// =============================================================================

export const getBulletins = async (): Promise<Bulletin[]> => {
  const response = await apiClient.get<Bulletin[]>('/bulletins');
  return response.data;
};

export const getBulletinById = async (id: number): Promise<Bulletin> => {
  const response = await apiClient.get<Bulletin>(`/bulletins/${id}`);
  return response.data;
};

export const getBulletinByServiceId = async (serviceId: number): Promise<Bulletin | null> => {
  try {
    const response = await apiClient.get<Bulletin>(`/bulletins/by-service/${serviceId}`);
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      return null;
    }
    throw error;
  }
};

export const createBulletin = async (data: BulletinInput): Promise<Bulletin> => {
  const response = await apiClient.post<Bulletin>('/bulletins', data);
  return response.data;
};

export const updateBulletin = async (id: number, data: Partial<BulletinInput>): Promise<Bulletin> => {
  const response = await apiClient.put<Bulletin>(`/bulletins/${id}`, data);
  return response.data;
};

export const deleteBulletin = async (id: number): Promise<{ success: boolean }> => {
  const response = await apiClient.delete<{ success: boolean }>(`/bulletins/${id}`);
  return response.data;
};

// =============================================================================
// PDF GENERATION
// =============================================================================

export const generatePdfPuppeteer = async (bulletinId: number, variant: '8am' | '10am' = '10am'): Promise<Blob> => {
  const response = await apiClient.post(
    `/bulletins/${bulletinId}/generate/puppeteer`,
    {},
    {
      params: { variant },
      responseType: 'blob'
    }
  );
  return response.data;
};

export const generatePdfLatex = async (bulletinId: number, variant: '8am' | '10am' = '10am'): Promise<Blob> => {
  const response = await apiClient.post(
    `/bulletins/${bulletinId}/generate/latex`,
    {},
    {
      params: { variant },
      responseType: 'blob'
    }
  );
  return response.data;
};

export const getHtmlPreview = async (bulletinId: number, variant: '8am' | '10am' = '10am'): Promise<string> => {
  const response = await apiClient.get<string>(`/bulletins/${bulletinId}/preview/html`, {
    params: { variant }
  });
  return response.data;
};

export const getLatexPreview = async (bulletinId: number, variant: '8am' | '10am' = '10am'): Promise<string> => {
  const response = await apiClient.get<string>(`/bulletins/${bulletinId}/preview/latex`, {
    params: { variant }
  });
  return response.data;
};

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

export const downloadPdf = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// =============================================================================
// SERVICE DATA FOR BULLETIN CREATION
// =============================================================================

export interface UpcomingService {
  service_id: number;
  date: string;
  name: string;
  time: string;
  ot_reading: string | null;
  nt_reading: string | null;
  gospel_reading: string | null;
  psalm_reading: string | null;
  special_service_name: string | null;
  season: string;
  liturgical_year: string;
  church_name: string;
}

export interface BulletinFile {
  file_id: number;
  file_path: string;
  file_type: string;
  canonical_filename: string;
}

export interface ServiceSong {
  service_selection_id: number;
  song_id: number;
  version_id: number | null;
  order_number: number;
  placement: string;
  performance_type: string | null;
  is_template_music: boolean;
  song_title: string;
  copyright: string | null;
  composer_name: string | null;
  lyricist_name: string | null;
  version_title: string | null;
  version_number: string | null;
  tune_name: string | null;
  bulletin_file: BulletinFile | null;
}

export interface ServiceDataResponse {
  service: {
    service_id: number;
    date: string;
    liturgy_name: string;
    time: string;
    ot_reading: string | null;
    nt_reading: string | null;
    gospel_reading: string | null;
    psalm_reading: string | null;
    special_service_name: string | null;
    church_id: number;
    season: string;
    liturgical_year: string;
    church_name: string;
  };
  readings: {
    first_reading: string | null;
    psalm: string | null;
    second_reading: string | null;
    gospel: string | null;
  };
  songs: ServiceSong[];
  songsByPlacement: Record<string, ServiceSong[]>;
  templateMusic: Record<string, {
    song_placement: string;
    song_title: string;
    composer_name: string | null;
    version_id: number | null;
    version_title: string | null;
    bulletin_file: BulletinFile | null;
  }>;
}

export const getUpcomingServices = async (options?: {
  churchId?: number;
  limit?: number;
}): Promise<UpcomingService[]> => {
  const params: any = {};
  if (options?.churchId !== undefined) {
    params.churchId = options.churchId;
  }
  if (options?.limit !== undefined) {
    params.limit = options.limit;
  }
  const response = await apiClient.get<UpcomingService[]>('/bulletins/upcoming-services', { params });
  return response.data;
};

export const getServiceData = async (serviceId: number): Promise<ServiceDataResponse> => {
  const response = await apiClient.get<ServiceDataResponse>(`/bulletins/service-data/${serviceId}`);
  return response.data;
};

// =============================================================================
// ONE-CLICK IDEAL BULLETIN GENERATION
// =============================================================================

export interface GenerateIdealOptions {
  variant?: '8am' | '10am';
  skipMissingFiles?: boolean;
}

export const generateIdealBulletin = async (
  serviceId: number,
  options: GenerateIdealOptions = {}
): Promise<Blob> => {
  const { variant = '10am', skipMissingFiles = true } = options;
  const response = await apiClient.post(
    `/bulletins/services/${serviceId}/generate-ideal`,
    {},
    {
      params: { variant, skipMissingFiles },
      responseType: 'blob'
    }
  );
  return response.data;
};
